// ─── Группы настроек: влияют ли на пересборку раундов ────────────────────────
//
// ROUND_AFFECTING_SETTINGS — при изменении любого из этих полей (или состава
// игроков) нужно вызывать regenerateUpcoming после сохранения.
//
// NON_ROUND_AFFECTING_SETTINGS — не требуют пересборки уже собранных раундов:
//   • rankingMode      — способ ранжирования таблицы лидеров
//   • matchScoring     — способ ввода счёта (TIME / POINTS), стратегия его не читает
//   • matchDurationMinutes — отображение таймера, к структуре раундов не относится
//
// ВАЖНО (rankingMode): «не влияет на пересборку» ≠ «можно менять всегда». rankingMode
// определяет места, а для Mexicano/Beat the Box ещё и входит в генерацию расписания
// (пары следующего тура / ярусы этапа 2 строятся по текущему рейтингу). Поэтому после
// первого сыгранного матча его менять НЕЛЬЗЯ — селектор блокируется (RankingModeField
// disabled при hasAnyMatchPlayed), а бэкенд отклоняет смену в RUNNING при сыгранных
// матчах. В DRAFT и в RUNNING до первого матча — меняется свободно.
//
// При добавлении нового поля настроек — явно укажите, к какой группе оно относится.
//
// ВАЖНО (Beat the Box): courtsCount жёстко привязан к составу игроков (players/4).
// Менять его отдельно после старта BtB-турнира бессмысленно и запрещено бэкендом.
// В DraftSettings/TournamentEditView можно добавить дизейбл для BtB.

export const ROUND_AFFECTING_SETTINGS = new Set([
  'courtsCount',
]);

export const NON_ROUND_AFFECTING_SETTINGS = new Set([
  'rankingMode',
  'matchScoring',
  'matchDurationMinutes',
]);

// ─── Вспомогательная функция ──────────────────────────────────────────────────

export const hasAnyMatchPlayed = (tournament) =>
  (tournament.rounds || []).some(r =>
    (r.matches || []).some(m => m.status !== 'PENDING')
  );

// ─── Посев игроков (random / manual order) ───────────────────────────────────
//
// Форматы, для которых начальный порядок игроков имеет смысл (используют seed):
// Mexicano (баланс тура 1) и Beat the Box (распределение по боксам этапа 1).
// Americano порядок не использует — тумблер для него скрыт (бэк хранит randomSeeding=null).
export const FORMATS_WITH_SEEDING = new Set(['MEXICANO', 'BEAT_THE_BOX']);

export const formatSupportsSeeding = (format) => FORMATS_WITH_SEEDING.has(format);

// ─── Beat the Box: хелперы ────────────────────────────────────────────────────

// Этап по номеру раунда: 1–3 → этап 1 (Group Play), 4–6 → этап 2 (Regrouping).
export const btbStageOf = (roundNumber) => roundNumber <= 3 ? 1 : 2;

// Лейбл группы этапа 2 по courtNumber (1-based).
// Court 1 → Gold, 2 → Silver, 3 → Bronze, 4 → Crystal.
export const BTB_GROUP_LABELS = {
  1: 'Gold',
  2: 'Silver',
  3: 'Bronze',
  4: 'Crystal',
};
export const btbGroupLabel = (courtNumber) => BTB_GROUP_LABELS[courtNumber] ?? `Группа ${courtNumber}`;

// Лейбл бокса этапа 1 по courtNumber (1-based): «Бокс 1», «Бокс 2», …
export const btbBoxLabel = (courtNumber) => `Бокс ${courtNumber}`;

// ─── Лейблы ───────────────────────────────────────────────────────────────────

export const SPORT_LABELS = {
  PICKLEBALL:       'Пиклбол',
  PADEL:            'Падел',
  TENNIS:           'Теннис',
  BADMINTON:        'Бадминтон',
  BEACH_VOLLEYBALL: 'Пляжный волейбол',
  SQUASH:           'Сквош',
  TABLE_TENNIS:     'Настольный теннис',
};

export const FORMAT_LABELS = {
  AMERICANO:     'Americano',
  MEXICANO:      'Mexicano',
  BEAT_THE_BOX:  'Beat the Box',
  KING_OF_COURT: 'King of the Court',
  ROUND_ROBIN:   'Round Robin',
};

// ─── Конфиги статусов ─────────────────────────────────────────────────────────

export const STATUS_CONFIG = {
  DRAFT:     { label: 'Черновик', bg: '#f3f4f6', border: '#d1d5db', color: '#6b7280' },
  RUNNING:   { label: 'Идёт',     bg: '#dcfce7', border: '#86efac', color: '#15803d' },
  FINISHED:  { label: 'Завершён', bg: '#ede9fe', border: '#c4b5fd', color: '#6d28d9' },
  CANCELLED: { label: 'Отменён',  bg: '#fee2e2', border: '#fca5a5', color: '#b91c1c' },
};

export const ROUND_STATUS_CONFIG = {
  PENDING:     { label: 'Ожидает',    bg: '#f3f4f6', color: '#6b7280' },
  IN_PROGRESS: { label: 'Идёт',       bg: '#fef9c3', color: '#854d0e' },
  FINISHED:    { label: 'Завершён',   bg: '#dcfce7', color: '#15803d' },
};

// ─── Режим таблицы лидеров ────────────────────────────────────────────────────

export const RANKING_OPTIONS = [
  { value: 'POINTS',   label: 'Сумма очков' },
  { value: 'FOOTBALL', label: 'По победам' },
];

export const RANKING_HINTS = {
  POINTS:
    'Игроки ранжируются по сумме набранных очков. При равенстве — по разнице очков (сумма «выиграл минус проиграл» по всем матчам).',
  FOOTBALL:
    'Игроки ранжируются по числу побед (победа — 1, ничья и поражение — 0). При равенстве — по разнице очков, затем по сумме набранных.',
};
